/* global FaceDetector */
import React, { useState, useRef, useEffect } from 'react';
import {
  Heart, Activity, Zap, Camera, CheckCircle, AlertCircle, Clock,
  TrendingUp, TrendingDown, Droplets, ShieldCheck, BarChart3, Thermometer,
} from 'lucide-react';
import './HealthMonitorApp.css';
import PatientDetailsModal from './PatientDetailsModal';
import GenerateReportButton from './GenerateReportButton';

const CHECK_ITEMS = [
  { key: 'faceDetected',    label: 'Face Detected' },
  { key: 'singleFace',      label: 'Single Face' },
  { key: 'correctDistance', label: 'Correct Distance (\u224830 cm)' },
  { key: 'foreheadVisible', label: 'Full Forehead Visible' },
  { key: 'faceCentered',    label: 'Face Centered' },
  { key: 'goodLighting',    label: 'Good Lighting' },
  { key: 'holdStill',       label: 'Hold Still' },
];

const FACE_WIDTH_TARGET_MIN = 60;
const FACE_WIDTH_TARGET_MAX = 220;
const CENTER_MARGIN_RATIO = 0.2;
const LIGHTING_MIN = 40;
const LIGHTING_MAX = 210;
const STILL_HISTORY_FRAMES = 15;
const STILL_MOVE_THRESHOLD = 4;

const HealthMonitorApp = () => {
  const [stage, setStage] = useState('idle');
  const [recordingTime, setRecordingTime] = useState(0);
  const [progress, setProgress] = useState(0);
  const [elapsedProcessing, setElapsedProcessing] = useState(0);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [showPatientModal, setShowPatientModal] = useState(false);
  const [patient, setPatient] = useState(null);

  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const uploadingRef = useRef(false);
  const processingTimerRef = useRef(null);
  const validationCanvasRef = useRef(null);
  const faceHistoryRef = useRef([]);
  const countdownTimerRef = useRef(null);
  const faceDetectorRef = useRef(null);
  const animFrameRef = useRef(null);
  const lastValidationTimeRef = useRef(0);
  const checksRef = useRef(Object.fromEntries(CHECK_ITEMS.map(c => [c.key, 'pending'])));
  const allReadyRef = useRef(false);
  const countdownActiveRef = useRef(false);
  const hintRef = useRef('');
  const readyCountdownRef = useRef(null);
  const streamRef = useRef(null);

  const [hintDisplay, setHintDisplay] = useState('');
  const [allReadyDisplay, setAllReadyDisplay] = useState(false);
  const [instructionStep, setInstructionStep] = useState(-1);
  const instructionTimerRef = useRef(null);
  const [cameraLoading, setCameraLoading] = useState(false);
  const [faceDetectedDisplay, setFaceDetectedDisplay] = useState('pending');
  const [scanReadyDisplay, setScanReadyDisplay] = useState(false);
  const [recCountdown, setRecCountdown] = useState(null);
  const [showRecComplete, setShowRecComplete] = useState(false);
  const recCountdownTimerRef = useRef(null);

  useEffect(() => {
    if (typeof FaceDetector !== 'undefined') {
      try {
        faceDetectorRef.current = new FaceDetector({ fastMode: true, maxDetectedFaces: 3 });
      } catch { /* ignore */ }
    }
  }, []);

  const updateDisplay = () => {
    setHintDisplay(hintRef.current);
    setAllReadyDisplay(allReadyRef.current);
  };

  const scheduleDisplayUpdate = () => {
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    animFrameRef.current = requestAnimationFrame(() => {
      animFrameRef.current = null;
      updateDisplay();
    });
  };

  const runValidation = async (timestamp) => {
    if (!lastValidationTimeRef.current) lastValidationTimeRef.current = timestamp;
    if (timestamp - lastValidationTimeRef.current < 200) return;
    lastValidationTimeRef.current = timestamp;

    const video = videoRef.current;
    const s = streamRef.current;
    if (!video || !video.videoWidth || !s) return;

    const vw = video.videoWidth;
    const vh = video.videoHeight;

    if (faceDetectorRef.current) {
      try {
        const faces = await faceDetectorRef.current.detect(video);
        processFaces(faces, vw, vh, video);
        requestAnimationFrame(runValidation);
        return;
      } catch { /* fall through */ }
    }

    if (faceDetectorRef.current) {
      processCanvasFallback(vw, vh, video);
    } else {
      autoPassAll();
    }
    requestAnimationFrame(runValidation);
  };

  const processFaces = (faces, vw, vh, video) => {
    const checks = checksRef.current;
    const faceCount = faces.length;
    let changed = false;

    const set = (key, val) => {
      if (checks[key] !== val) { checks[key] = val; changed = true; }
    };

    set('faceDetected', faceCount >= 1 ? 'pass' : 'fail');
    set('singleFace', faceCount === 1 ? 'pass' : 'fail');

    if (faceCount === 0) {
      hintRef.current = 'No face detected. Please position your face within the camera frame.';
      faceHistoryRef.current = [];
      set('correctDistance', 'pending');
      set('foreheadVisible', 'pending');
      set('faceCentered', 'pending');
      set('goodLighting', 'pending');
      set('holdStill', 'pending');
      allReadyRef.current = false;
      countdownActiveRef.current = false;
      if (countdownTimerRef.current) { clearInterval(countdownTimerRef.current); countdownTimerRef.current = null; }
      readyCountdownRef.current = null;
      if (changed) scheduleDisplayUpdate();
      return;
    }

    if (faceCount > 1) {
      hintRef.current = 'Multiple faces detected. Please ensure only one person is visible.';
      set('correctDistance', 'pending');
      set('foreheadVisible', 'pending');
      set('faceCentered', 'pending');
      set('goodLighting', 'pending');
      set('holdStill', 'pending');
      allReadyRef.current = false;
      countdownActiveRef.current = false;
      if (countdownTimerRef.current) { clearInterval(countdownTimerRef.current); countdownTimerRef.current = null; }
      readyCountdownRef.current = null;
      if (changed) scheduleDisplayUpdate();
      return;
    }

    const face = faces[0];
    const box = face.boundingBox;
    const cx = box.x + box.width / 2;
    const cy = box.y + box.height / 2;

    const distOk = box.width >= FACE_WIDTH_TARGET_MIN && box.width <= FACE_WIDTH_TARGET_MAX;
    set('correctDistance', distOk ? 'pass' : 'fail');

    const topInFrame = box.y > vh * 0.02;
    const foreheadRegion = box.y - box.height * 0.15;
    const foreheadVisible = topInFrame && foreheadRegion > 0;
    set('foreheadVisible', foreheadVisible ? 'pass' : 'fail');

    const cxMargin = vw * CENTER_MARGIN_RATIO;
    const cyMargin = vh * CENTER_MARGIN_RATIO;
    const centerXOk = Math.abs(cx - vw / 2) < cxMargin;
    const centerYOk = Math.abs(cy - vh / 2) < cyMargin;
    set('faceCentered', (centerXOk && centerYOk) ? 'pass' : 'fail');

    const lightingResult = analyzeLightingForFace(video, box);
    set('goodLighting', lightingResult === 'pass' ? 'pass' : 'fail');

    faceHistoryRef.current.push({ cx, cy });
    if (faceHistoryRef.current.length > STILL_HISTORY_FRAMES) faceHistoryRef.current.shift();

    if (faceHistoryRef.current.length >= 5) {
      const variance = computeVariance(faceHistoryRef.current);
      set('holdStill', variance < STILL_MOVE_THRESHOLD ? 'pass' : 'fail');
    } else {
      set('holdStill', 'pending');
    }

    let hint = '';
    if (!distOk) hint = box.width < FACE_WIDTH_TARGET_MIN
      ? 'Please move closer to the camera. Maintain approximately 30 cm distance for accurate measurement.'
      : 'Please move slightly farther from the camera.';
    else if (!foreheadVisible) hint = 'Please ensure your full forehead is clearly visible in the camera.';
    else if (!centerXOk || !centerYOk) hint = 'Please center your face within the camera frame.';
    else if (lightingResult === 'tooDark') hint = 'Lighting is too low. Please move to a well-lit area or face a light source.';
    else if (lightingResult === 'tooBright') hint = 'Lighting is too bright. Please reduce direct light or move to balanced lighting.';
    else if (checks.holdStill !== 'pass') hint = 'Please hold still while we prepare the measurement.';
    else hint = '';

    hintRef.current = hint;

    const ready = Object.values(checks).every(s => s === 'pass');
    if (ready !== allReadyRef.current) {
      allReadyRef.current = ready;
      if (ready) {
        if (!countdownActiveRef.current) startCountdown();
      } else {
        countdownActiveRef.current = false;
        if (countdownTimerRef.current) { clearInterval(countdownTimerRef.current); countdownTimerRef.current = null; }
        readyCountdownRef.current = null;
      }
      changed = true;
    }

    setFaceDetectedDisplay(checks.faceDetected === 'pass' ? 'yes' : checks.faceDetected === 'fail' ? 'no' : 'pending');
    if (changed) scheduleDisplayUpdate();
  };

  const processCanvasFallback = (vw, vh, video) => {
    const canvas = validationCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    canvas.width = vw;
    canvas.height = vh;
    ctx.drawImage(video, 0, 0, vw, vh);

    const imageData = ctx.getImageData(0, 0, vw, vh);
    const pixels = imageData.data;
    const brightness = computeAverageBrightness(pixels);
    const checks = checksRef.current;
    let changed = false;

    const set = (key, val) => {
      if (checks[key] !== val) { checks[key] = val; changed = true; }
    };

    const hasMotion = detectMotionFallback(pixels, vw, vh);
    const brightEnough = brightness > LIGHTING_MIN;
    const notOverexposed = brightness < LIGHTING_MAX;

    set('goodLighting', brightEnough && notOverexposed ? 'pass' : 'fail');
    set('holdStill', !hasMotion ? 'pass' : 'fail');

    const faceRegion = detectFaceByBrightness(pixels, vw, vh);
    if (faceRegion) {
      const { x, y, w, h } = faceRegion;
      set('faceDetected', 'pass');
      set('singleFace', 'pass');

      const distOk = w >= FACE_WIDTH_TARGET_MIN && w <= FACE_WIDTH_TARGET_MAX;
      set('correctDistance', distOk ? 'pass' : 'fail');

      const foreheadVisible = y > vh * 0.02;
      set('foreheadVisible', foreheadVisible ? 'pass' : 'fail');

      const cx = x + w / 2;
      const cy = y + h / 2;
      const cxMargin = vw * CENTER_MARGIN_RATIO;
      const cyMargin = vh * CENTER_MARGIN_RATIO;
      set('faceCentered', (Math.abs(cx - vw / 2) < cxMargin && Math.abs(cy - vh / 2) < cyMargin) ? 'pass' : 'fail');

      let hint = '';
      if (!distOk) hint = w < FACE_WIDTH_TARGET_MIN
        ? 'Please move closer to the camera. Maintain approximately 30 cm distance for accurate measurement.'
        : 'Please move slightly farther from the camera.';
      else if (!foreheadVisible) hint = 'Please ensure your full forehead is clearly visible in the camera.';
      else if (checks.faceCentered !== 'pass') hint = 'Please center your face within the camera frame.';
      else if (!brightEnough) hint = 'Lighting is too low. Please move to a well-lit area or face a light source.';
      else if (!notOverexposed) hint = 'Lighting is too bright. Please reduce direct light or move to balanced lighting.';
      else if (hasMotion) hint = 'Please hold still while we prepare the measurement.';
      else hint = '';
      hintRef.current = hint;
    } else {
      set('faceDetected', 'fail');
      set('singleFace', 'pending');
      set('correctDistance', 'pending');
      set('foreheadVisible', 'pending');
      set('faceCentered', 'pending');
      hintRef.current = 'No face detected. Please position your face within the camera frame.';
    }

    const ready = Object.values(checks).every(s => s === 'pass');
    if (ready !== allReadyRef.current) {
      allReadyRef.current = ready;
      if (ready) {
        if (!countdownActiveRef.current) startCountdown();
      } else {
        countdownActiveRef.current = false;
        if (countdownTimerRef.current) { clearInterval(countdownTimerRef.current); countdownTimerRef.current = null; }
        readyCountdownRef.current = null;
      }
      changed = true;
    }

    setFaceDetectedDisplay(checks.faceDetected === 'pass' ? 'yes' : checks.faceDetected === 'fail' ? 'no' : 'pending');
    if (changed) scheduleDisplayUpdate();
  };

  const analyzeLightingForFace = (video, box) => {
    const canvas = validationCanvasRef.current;
    if (!canvas) return 'fail';
    const ctx = canvas.getContext('2d');
    const margin = 10;
    const x = Math.max(0, box.x - margin);
    const y = Math.max(0, box.y - margin);
    const w = Math.min(box.width + margin * 2, video.videoWidth - x);
    const h = Math.min(box.height + margin * 2, video.videoHeight - y);
    if (w <= 0 || h <= 0) return 'fail';

    ctx.drawImage(video, x, y, w, h, 0, 0, w, h);
    const imageData = ctx.getImageData(0, 0, w, h);
    const pixels = imageData.data;

    let sum = 0, count = 0;
    for (let i = 0; i < pixels.length; i += 4) {
      const b = 0.299 * pixels[i] + 0.587 * pixels[i + 1] + 0.114 * pixels[i + 2];
      sum += b; count++;
    }
    const avg = sum / count;
    if (avg < LIGHTING_MIN) return 'tooDark';
    if (avg > LIGHTING_MAX) return 'tooBright';
    return 'pass';
  };

  const computeVariance = (history) => {
    const n = history.length;
    if (n < 2) return Infinity;
    const meanX = history.reduce((s, p) => s + p.cx, 0) / n;
    const meanY = history.reduce((s, p) => s + p.cy, 0) / n;
    const varX = history.reduce((s, p) => s + (p.cx - meanX) ** 2, 0) / n;
    const varY = history.reduce((s, p) => s + (p.cy - meanY) ** 2, 0) / n;
    return Math.sqrt(varX + varY);
  };

  const computeAverageBrightness = (pixels) => {
    let sum = 0, count = 0;
    for (let i = 0; i < pixels.length; i += 4) {
      sum += 0.299 * pixels[i] + 0.587 * pixels[i + 1] + 0.114 * pixels[i + 2];
      count++;
    }
    return sum / count;
  };

  const detectMotionFallback = (pixels, w, h) => {
    if (!faceHistoryRef.current.length) {
      faceHistoryRef.current.push(pixels.slice(0, 100));
      return true;
    }
    const prev = faceHistoryRef.current[faceHistoryRef.current.length - 1];
    const sampleSize = Math.min(pixels.length, 500);
    let diff = 0;
    for (let i = 0; i < sampleSize; i += 4) {
      diff += Math.abs(pixels[i] - prev[i]);
    }
    faceHistoryRef.current.push(pixels.slice(0, 100));
    if (faceHistoryRef.current.length > 5) faceHistoryRef.current.shift();
    return diff / sampleSize > 15;
  };

  const detectFaceByBrightness = (pixels, w, h) => {
    const brightnessMap = [];
    const blockSize = 8;
    for (let y = 0; y < h; y += blockSize) {
      for (let x = 0; x < w; x += blockSize) {
        let sum = 0, count = 0;
        for (let dy = 0; dy < blockSize && y + dy < h; dy++) {
          for (let dx = 0; dx < blockSize && x + dx < w; dx++) {
            const i = ((y + dy) * w + (x + dx)) * 4;
            sum += 0.299 * pixels[i] + 0.587 * pixels[i + 1] + 0.114 * pixels[i + 2];
            count++;
          }
        }
        brightnessMap.push({ x, y, v: sum / count });
      }
    }

    const skinLike = brightnessMap.filter(b => b.v > LIGHTING_MIN && b.v < LIGHTING_MAX);
    if (skinLike.length < 20) return null;

    const minX = Math.min(...skinLike.map(b => b.x));
    const maxX = Math.max(...skinLike.map(b => b.x));
    const minY = Math.min(...skinLike.map(b => b.y));
    const maxY = Math.max(...skinLike.map(b => b.y));
    const fw = maxX - minX;
    const fh = maxY - minY;
    if (fw < 30 || fh < 40) return null;

    return { x: minX, y: minY, w: fw, h: fh };
  };

  const autoPassAll = () => {
    const checks = checksRef.current;
    let changed = false;
    CHECK_ITEMS.forEach(item => {
      if (checks[item.key] !== 'pass') { checks[item.key] = 'pass'; changed = true; }
    });
    hintRef.current = '';
    if (!allReadyRef.current) {
      allReadyRef.current = true;
      changed = true;
    }
    if (changed) scheduleDisplayUpdate();
  };

  const startCountdown = () => {
    countdownActiveRef.current = true;
    setScanReadyDisplay(true);
    setTimeout(() => {
      setScanReadyDisplay(false);
      let count = 3;
      setRecCountdown(count);
      recCountdownTimerRef.current = setInterval(() => {
        count--;
        if (count <= 0) {
          clearInterval(recCountdownTimerRef.current);
          recCountdownTimerRef.current = null;
          setRecCountdown(null);
          startRecordingInternal();
        } else {
          setRecCountdown(count);
        }
      }, 1000);
    }, 2000);
  };

  const startRecordingInternal = () => {
    if (!streamRef.current) return;
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') return;
    chunksRef.current = [];
    const mimeTypes = ['video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm'];
    let selectedMime = '';
    for (const m of mimeTypes) {
      if (MediaRecorder.isTypeSupported(m)) { selectedMime = m; break; }
    }
    const mediaRecorder = new MediaRecorder(streamRef.current, selectedMime ? { mimeType: selectedMime } : {});
    mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    mediaRecorder.onstop = () => {};
    mediaRecorderRef.current = mediaRecorder;
    mediaRecorder.start();
    setStage('recording');
    setRecordingTime(0);
    timerRef.current = setInterval(() => {
      setRecordingTime(prev => {
        const newTime = prev + 1;
        if (newTime >= 30) { stopRecording(); }
        return newTime;
      });
    }, 1000);
  };

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: "user" },
        audio: false
      });
      streamRef.current = mediaStream;
      if (videoRef.current) {
        
        videoRef.current.srcObject = mediaStream;
        videoRef.current.play().catch(e => console.error('Video play error:', e));
      }
    } catch (err) {
      setError('Could not access camera. Please check permissions.');
      console.error('Camera error:', err);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') mediaRecorderRef.current.stop();
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    setShowRecComplete(true);
    setTimeout(() => {
      setShowRecComplete(false);
      if (chunksRef.current.length > 0) {
        const blob = new Blob(chunksRef.current, { type: 'video/webm' });
        uploadVideo(blob);
      }
    }, 1500);
  };

  const uploadVideo = async (blob) => {
    if (uploadingRef.current) return;
    uploadingRef.current = true;
    setStage('processing');
    setProgress(0);
    setElapsedProcessing(0);

    const TOTAL_WAIT_MS = 600000;
    const INTERVAL_MS = 3000;
    const progressInterval = setInterval(() => {
      setProgress(prev => Math.min(prev + (90 / (TOTAL_WAIT_MS / INTERVAL_MS)), 90));
    }, INTERVAL_MS);

    processingTimerRef.current = setInterval(() => setElapsedProcessing(prev => prev + 1), 1000);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 600000);
    const delay = (ms) => new Promise(res => setTimeout(res, ms));
    const startTime = Date.now();

    const attemptFetch = async () => {
      const formData = new FormData();
      formData.append('video', blob, 'recording.webm');
      const scanUser = localStorage.getItem('user_id');
      if (scanUser) formData.append('scan_user', scanUser);

      const response = await fetch('https://catbackend.xraidigital.com/api/vitals/analyze-vitals/', {
        method: 'POST', body: formData, signal: controller.signal,
      });
      // const response = await fetch('http://127.0.0.1:8000/api/vitals/analyze-vitals/', {
      //   method: 'POST', body: formData, signal: controller.signal,
      // });
      if ([502, 503, 504].includes(response.status)) {
        const elapsed = Date.now() - startTime;
        if (elapsed < 600000) { await delay(60000); return attemptFetch(); }
        else throw new Error(`Server temporarily unavailable (${response.status})`);
      }
      return response;
    };

    try {
      const response = await attemptFetch();
      clearTimeout(timeoutId); clearInterval(progressInterval); clearInterval(processingTimerRef.current);
      setProgress(100);
      const data = await response.json();
      if (data.success && data.data) {
        const apiData = data.data;
        setResults({
          heart_rate: apiData.heart_rate || 0, bp_systolic: apiData.bp_systolic || 0,
          bp_diastolic: apiData.bp_diastolic || 0, hrv_sdnn: apiData.hrv_sdnn || 0,
          hrv_rmssd: apiData.hrv_rmssd || 0, duration: apiData.duration || 30,
          frames_analyzed: apiData.frames_analyzed || 0, confidence: apiData.confidence || 0,
          scan_id: apiData.id || 0, spo2: apiData.spo2 || 0, resp_rate: apiData.resp_rate || 0,
          training_data_id: apiData.training_data_id || 0, hemoglobin: apiData.hemoglobin || 0,
          all_vital_results: apiData,
        });
        setStage('results');
        stopCamera();
      } else {
        setError(data.error || 'Analysis failed');
        setStage('idle');
      }
    } catch (err) {
      clearTimeout(timeoutId); clearInterval(progressInterval); clearInterval(processingTimerRef.current);
      uploadingRef.current = false;
      if (err.name === 'AbortError') setError('Analysis timed out after 5 minutes. Please try again.');
      else setError('Could not connect to server. Make sure Django backend is running.');
      setStage('idle');
    }
  };

  const startValidationLoop = () => {
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    faceHistoryRef.current = [];
    allReadyRef.current = false;
    countdownActiveRef.current = false;
    readyCountdownRef.current = null;
    hintRef.current = '';
    const initial = Object.fromEntries(CHECK_ITEMS.map(c => [c.key, 'pending']));
    checksRef.current = initial;
    lastValidationTimeRef.current = 0;
    updateDisplay();
    requestAnimationFrame(runValidation);
  };

  const reset = () => {
    setStage('idle'); setRecordingTime(0); setProgress(0); setElapsedProcessing(0);
    setResults(null); setError(null); stopCamera(); setPatient(null); setShowPatientModal(false);
    if (processingTimerRef.current) { clearInterval(processingTimerRef.current); processingTimerRef.current = null; }
    if (countdownTimerRef.current) { clearInterval(countdownTimerRef.current); countdownTimerRef.current = null; }
    if (animFrameRef.current) { cancelAnimationFrame(animFrameRef.current); animFrameRef.current = null; }
    if (recCountdownTimerRef.current) { clearInterval(recCountdownTimerRef.current); recCountdownTimerRef.current = null; }
    setCameraLoading(false);
    setFaceDetectedDisplay('pending');
    setScanReadyDisplay(false);
    setRecCountdown(null);
    setShowRecComplete(false);
    uploadingRef.current = false;
    allReadyRef.current = false;
    countdownActiveRef.current = false;
    readyCountdownRef.current = null;
    hintRef.current = '';
    const initial = Object.fromEntries(CHECK_ITEMS.map(c => [c.key, 'pending']));
    checksRef.current = initial;
    updateDisplay();
  };

  const handleStartCamera = async () => {
    setCameraLoading(true);
    await startCamera();
    if (streamRef.current) {
      setTimeout(() => {
        setCameraLoading(false);
        setStage('camera');
      }, 800);
    } else {
      setCameraLoading(false);
    }
  };

  const handlePatientSubmit = (details) => {
    setPatient(details);
    setShowPatientModal(false);
    handleStartCamera();
  };

  useEffect(() => {
    if ((stage === 'camera' || stage === 'recording') && streamRef.current && videoRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(e => console.error('Video play error:', e));
      if (stage === 'camera') {
        videoRef.current.onloadeddata = () => {
          setTimeout(startValidationLoop, 500);
        };
      }
    }
  }, [stage]);

  const INSTRUCTIONS = [
    { icon: '\uD83D\uDCCF', text: 'Move within 30 cm of the camera' },
    { icon: '\uD83D\uDC64', text: 'Keep your full forehead visible' },
    { icon: '\uD83C\uDFAF', text: 'Center your face inside the guide' },
    { icon: '\u270B', text: 'Hold still' },
  ];

  useEffect(() => {
    if (stage === 'camera') {
      setInstructionStep(0);
      instructionTimerRef.current = setInterval(() => {
        setInstructionStep(prev => {
          if (prev >= INSTRUCTIONS.length - 1) {
            clearInterval(instructionTimerRef.current);
            instructionTimerRef.current = null;
            return -1;
          }
          return prev + 1;
        });
      }, 2000);
    } else {
      setInstructionStep(-1);
      if (instructionTimerRef.current) {
        clearInterval(instructionTimerRef.current);
        instructionTimerRef.current = null;
      }
    }
    return () => {
      if (instructionTimerRef.current) {
        clearInterval(instructionTimerRef.current);
        instructionTimerRef.current = null;
      }
    };
  }, [stage]);

  const getHeartRateStatus = (hr) => {
    if (hr < 60) return { label: 'Below Normal', color: 'status-info' };
    if (hr > 100) return { label: 'Above Normal', color: 'status-warning' };
    return { label: 'Normal', color: 'status-normal' };
  };
  const getHRVStatus = (hrv) => {
    if (hrv < 20) return { label: 'Low', color: 'status-warning' };
    if (hrv > 50) return { label: 'Excellent', color: 'status-normal' };
    return { label: 'Good', color: 'status-info' };
  };
  const getBPStatus = (sys, dia) => {
    if (sys < 120 && dia < 80) return { label: 'Normal', color: 'status-normal' };
    if (sys >= 130 || dia >= 80) return { label: 'Elevated', color: 'status-warning' };
    return { label: 'Pre-Hypertension', color: 'status-warning' };
  };
  const getHemoglobinStatus = (hb) => {
    if (hb <= 0) return { label: 'N/A', color: 'status-muted' };
    if (hb < 12) return { label: 'Low', color: 'status-warning' };
    if (hb > 17.5) return { label: 'High', color: 'status-warning' };
    return { label: 'Normal', color: 'status-normal' };
  };
  const formatElapsed = (secs) => { const m = Math.floor(secs / 60); return `${m}:${(secs % 60).toString().padStart(2, '0')}`; };

  const normalizeHR = (hr) => {
    if (hr >= 60 && hr <= 100) return 100;
    if (hr < 40 || hr > 140) return 0;
    if (hr < 60) return ((hr - 40) / 20) * 100;
    return Math.max(0, (1 - (hr - 100) / 40) * 100);
  };
  const normalizeBP = (sys, dia) => {
    const sScore = sys >= 90 && sys <= 120 ? 100 : sys < 90 ? Math.max(0, (sys - 70) / 20 * 100) : Math.max(0, (1 - (sys - 120) / 60) * 100);
    const dScore = dia >= 60 && dia <= 80 ? 100 : dia < 60 ? Math.max(0, (dia - 40) / 20 * 100) : Math.max(0, (1 - (dia - 80) / 40) * 100);
    return Math.round((sScore + dScore) / 2);
  };
  const normalizeSpO2 = (spo2) => { if (spo2 >= 95) return 100; if (spo2 <= 85) return 0; return ((spo2 - 85) / 10) * 100; };
  const normalizeHgb = (hgb) => { if (hgb <= 0) return 50; if (hgb >= 12 && hgb <= 17) return 100; if (hgb < 12) return Math.max(0, (hgb - 8) / 4 * 100); return Math.max(0, (1 - (hgb - 17) / 5) * 100); };
  const normalizeRR = (rr) => { if (rr >= 12 && rr <= 20) return 100; if (rr < 6 || rr > 30) return 0; if (rr < 12) return ((rr - 6) / 6) * 100; return Math.max(0, (1 - (rr - 20) / 10) * 100); };
  const normalizeHRV = (rmssd, sdnn) => {
    const rScore = rmssd >= 30 ? 100 : rmssd <= 10 ? 0 : ((rmssd - 10) / 20) * 100;
    const sScore = sdnn >= 50 ? 100 : sdnn <= 15 ? 0 : ((sdnn - 15) / 35) * 100;
    return Math.round((rScore + sScore) / 2);
  };

  const getRadarData = () => {
    if (!results) return [];
    const hrVal = normalizeHR(results.heart_rate);
    const bpVal = normalizeBP(results.bp_systolic, results.bp_diastolic);
    const spo2Val = normalizeSpO2(results.spo2);
    const hgbVal = normalizeHgb(results.hemoglobin);
    const rrVal = normalizeRR(results.resp_rate);
    const hrvVal = normalizeHRV(results.hrv_rmssd, results.hrv_sdnn);
    const hrvSdnnVal = results.hrv_sdnn >= 50 ? 100 : results.hrv_sdnn <= 15 ? 0 : ((results.hrv_sdnn - 15) / 35) * 100;
    return [
      { metric: 'Heart Rate', value: Math.round(hrVal), unit: 'BPM', raw: Math.round(results.heart_rate), icon: '\u2764\uFE0F', range: '60\u2013100 BPM', status: getHeartRateStatus(results.heart_rate).label, cls: hrVal >= 80 ? 'excellent' : 'normal' },
      { metric: 'Blood Pressure', value: Math.round(bpVal), unit: 'mmHg', raw: `${results.bp_systolic}/${results.bp_diastolic}`, icon: '\uD83E\uDE78', range: '120/80 mmHg', status: getBPStatus(results.bp_systolic, results.bp_diastolic).label, cls: bpVal >= 80 ? 'normal' : bpVal >= 50 ? 'fair' : 'low' },
      { metric: 'SpO\u2082', value: Math.round(spo2Val), unit: '%', raw: results.spo2.toFixed(1), icon: '\uD83E\uDE7B', range: '95\u2013100%', status: spo2Val >= 80 ? 'Normal' : 'Low', cls: spo2Val >= 80 ? 'normal' : 'low' },
      { metric: 'Hemoglobin', value: Math.round(hgbVal), unit: 'g/dL', raw: results.hemoglobin > 0 ? results.hemoglobin.toFixed(1) : '\u2014', icon: '\uD83E\uDE78', range: '12\u201317 g/dL', status: getHemoglobinStatus(results.hemoglobin).label, cls: hgbVal >= 80 ? 'normal' : hgbVal >= 50 ? 'fair' : 'low' },
      { metric: 'Respiratory Rate', value: Math.round(rrVal), unit: 'br/min', raw: results.resp_rate.toFixed(1), icon: '\uD83C\uDF1B', range: '12\u201320 br/min', status: rrVal >= 80 ? 'Normal' : rrVal >= 50 ? 'Fair' : 'Low', cls: rrVal >= 80 ? 'normal' : rrVal >= 50 ? 'fair' : 'low' },
      { metric: 'HRV RMSSD', value: Math.round(normalizeHRV(results.hrv_rmssd, results.hrv_rmssd)), unit: 'ms', raw: Math.round(results.hrv_rmssd), icon: '\u26A1', range: '>30 ms', status: getHRVStatus(results.hrv_rmssd).label, cls: hrvVal >= 80 ? 'excellent' : hrvVal >= 50 ? 'normal' : 'fair' },
      { metric: 'HRV SDNN', value: Math.round(hrvSdnnVal), unit: 'ms', raw: Math.round(results.hrv_sdnn), icon: '\uD83D\uDCC8', range: '>50 ms', status: getHRVStatus(results.hrv_sdnn).label, cls: hrvSdnnVal >= 80 ? 'excellent' : hrvSdnnVal >= 50 ? 'normal' : 'fair' },
    ];
  };

  const getHealthSummary = () => {
    if (!results) return { lines: ['No data available.'], score: 0 };
    try {
      const hr = results.heart_rate;
      const sys = results.bp_systolic;
      const dia = results.bp_diastolic;
      const spo2 = results.spo2;
      const hgb = results.hemoglobin;
      const rr = results.resp_rate;
      const hrv = results.hrv_rmssd;
      const lines = [];
      if (hr >= 60 && hr <= 100) lines.push('Heart rate is within the normal resting range.');
      else if (hr < 60) lines.push('Heart rate is slightly below the normal resting range.');
      else lines.push('Heart rate is elevated above the normal resting range.');
      if (sys < 120 && dia < 80) lines.push('Blood pressure is within the optimal range.');
      else if (sys < 130) lines.push('Blood pressure is slightly elevated but within acceptable limits.');
      else lines.push('Blood pressure is elevated and may benefit from monitoring.');
      if (spo2 >= 95) lines.push('Oxygen saturation is excellent and within the healthy range.');
      else if (spo2 >= 90) lines.push('Oxygen saturation is slightly below optimal.');
      else lines.push('Oxygen saturation is below the normal range.');
      if (hgb > 0) {
        if (hgb >= 12 && hgb <= 17) lines.push('Hemoglobin level is within the normal range.');
        else if (hgb < 12) lines.push('Hemoglobin appears below the recommended level.');
        else lines.push('Hemoglobin is slightly above the normal range.');
      }
      if (rr >= 12 && rr <= 20) lines.push('Respiratory rate is normal.');
      else if (rr < 12) lines.push('Respiratory rate is below normal.');
      else lines.push('Respiratory rate is elevated.');
      if (hrv >= 30) lines.push('HRV values indicate good recovery and autonomic balance.');
      else if (hrv >= 20) lines.push('HRV values suggest moderate recovery capacity.');
      else lines.push('HRV values indicate lower recovery capacity.');
      const radarData = getRadarData();
      const scores = radarData.length ? radarData.map(d => d.value) : [50];
      const avgScore = Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
      return { lines, score: avgScore };
    } catch {
      return { lines: ['Unable to generate health summary.'], score: 0 };
    }
  };

  const SvgRadarChart = () => {
    const data = getRadarData();
    if (!data.length) return null;
    const cx = 160, cy = 170, R = 120, levels = 5;
    const n = data.length;
    const angleStep = (2 * Math.PI) / n;
    const getPoint = (idx, radius) => {
      const angle = idx * angleStep - Math.PI / 2;
      return { x: cx + radius * Math.cos(angle), y: cy + radius * Math.sin(angle) };
    };
    const gridLines = Array.from({ length: levels }, (_, li) => {
      const r = (R * (li + 1)) / levels;
      const d = Array.from({ length: n }, (__, i) => getPoint(i, r)).map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join('') + 'Z';
      return <path key={li} d={d} fill="none" stroke="#E2E8F0" strokeWidth={1} />;
    });
    const axes = Array.from({ length: n }, (_, i) => {
      const p = getPoint(i, R);
      return <line key={`ax${i}`} x1={cx} y1={cy} x2={p.x.toFixed(1)} y2={p.y.toFixed(1)} stroke="#E2E8F0" strokeWidth={1} />;
    });
    const labels = data.map((d, i) => {
      const p = getPoint(i, R + 24);
      const isLeft = p.x < cx, isRight = p.x > cx, isTop = p.y < cy, isBottom = p.y > cy;
      let ta = 'middle';
      if (isLeft) ta = 'end'; else if (isRight) ta = 'start';
      return <text key={`lb${i}`} x={p.x.toFixed(1)} y={p.y.toFixed(1)} textAnchor={ta} dominantBaseline={isBottom ? 'top' : isTop ? 'auto' : 'central'} fontSize={10} fill="#64748B" fontWeight={500}>{d.metric}</text>;
    });
    const dataPoints = data.map((d, i) => {
      const r = (Math.min(Math.max(d.value, 0), 100) / 100) * R;
      return getPoint(i, r);
    });
    const polyPath = dataPoints.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join('') + 'Z';
    const dots = data.map((d, i) => {
      const p = dataPoints[i];
      return (
        <g key={`dt${i}`}>
          <circle cx={p.x.toFixed(1)} cy={p.y.toFixed(1)} r={5} fill="#0F766E" stroke="#fff" strokeWidth={2} style={{ cursor: 'pointer' }}>
            <title>{`${d.metric}: ${d.raw} ${d.unit} (${d.status})`}</title>
          </circle>
        </g>
      );
    });
    return (
      <svg viewBox="0 0 320 340" style={{ width: '100%', height: '100%', display: 'block' }}>
        {gridLines}
        {axes}
        {labels}
        <path d={polyPath} fill="#0F766E" fillOpacity={0.2} stroke="#0F766E" strokeWidth={2.5} className="hm-radar-polygon" />
        {dots}
      </svg>
    );
  };


  return (
    <div className="hm-app">
      <div className="hm-container">
        {(stage === 'idle' || stage === 'camera') && (
          <div className="hm-hero">
            <div className="hm-hero-icon">
              <Heart size={28} />
            </div>
            <h1 className="hm-hero-title">VitalScan</h1>
            <p className="hm-hero-sub">AI-Powered Vital Signs Analysis</p>
          </div>
        )}

        {error && (
          <div className="hm-error">
            <AlertCircle size={18} />
            <div>
              <p className="hm-error-title">Error</p>
              <p className="hm-error-msg">{error}</p>
            </div>
          </div>
        )}

        {stage === 'idle' && (
          <div className="hm-idle">
            <div className="hm-idle-icon-wrap">
              <div className="hm-idle-icon-bg" />
              <Camera size={40} className="hm-idle-icon" />
            </div>
            <h2 className="hm-idle-title">Ready to Analyze Your Vitals</h2>
            <p className="hm-idle-desc">
              Position your face in the frame and remain still for 30 seconds.
              Ensure you are in a well-lit environment for accurate results.
            </p>
            <button className="hm-btn hm-btn-primary" onClick={() => setShowPatientModal(true)}>
              <Camera size={18} />
              Start Camera
            </button>
            <div className="hm-features">
              <div className="hm-feature">
                <div className="hm-feature-icon hm-feature-1"><Heart size={20} /></div>
                <h3 className="hm-feature-title">Heart Rate</h3>
                <p className="hm-feature-desc">Real-time BPM</p>
              </div>
              <div className="hm-feature">
                <div className="hm-feature-icon hm-feature-2"><Activity size={20} /></div>
                <h3 className="hm-feature-title">HRV Metrics</h3>
                <p className="hm-feature-desc">Variability analysis</p>
              </div>
              <div className="hm-feature">
                <div className="hm-feature-icon hm-feature-3"><TrendingUp size={20} /></div>
                <h3 className="hm-feature-title">Blood Pressure</h3>
                <p className="hm-feature-desc">Systolic & Diastolic</p>
              </div>
              <div className="hm-feature">
                <div className="hm-feature-icon hm-feature-4"><Zap size={20} /></div>
                <h3 className="hm-feature-title">AI Analysis</h3>
                <p className="hm-feature-desc">Instant processing</p>
              </div>
            </div>
          </div>
        )}

        {cameraLoading && (
          <div className="hm-camera-loading">
            <div className="hm-loading-spinner" />
            <p className="hm-loading-text">Initializing AI Camera...</p>
          </div>
        )}

        {stage === 'camera' && instructionStep >= 0 && (
          <div className="hm-instruction-banner">
            <span className="hm-instruction-icon">{INSTRUCTIONS[instructionStep].icon}</span>
            <span className="hm-instruction-text" key={instructionStep}>
              {INSTRUCTIONS[instructionStep].text}
            </span>
          </div>
        )}

        {stage === 'camera' && (
          <div className="hm-camera hm-camera-enter">
            <div className="hm-camera-border" />
            <div className="hm-video-wrap">
              <video ref={videoRef} autoPlay playsInline muted className="hm-video hm-video-enter"
                style={{ transform: 'scaleX(-1)', backgroundColor: '#000' }} />
              <canvas ref={validationCanvasRef} style={{ display: 'none' }} />
              <div className="hm-video-overlay">
                <div className={`hm-face-guide ${faceDetectedDisplay === 'yes' ? 'face-detected' : faceDetectedDisplay === 'no' ? 'face-not-detected' : ''}`} />
              </div>
              {faceDetectedDisplay === 'no' && (
                <div className="hm-looking-text">Looking for your face...</div>
              )}
              {faceDetectedDisplay === 'yes' && allReadyDisplay && scanReadyDisplay && (
                <div className="hm-scan-ready">
                  <div className="hm-scan-ready-inner">
                    <div className="hm-ready-checkmark">
                      <svg viewBox="0 0 24 24" width="48" height="48"><path d="M5 13l4 4L19 7" fill="none" stroke="#10B981" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="hm-check-path" /></svg>
                    </div>
                    <p className="hm-ready-text">Ready to Scan</p>
                  </div>
                </div>
              )}
              {allReadyDisplay && recCountdown !== null && !scanReadyDisplay && (
                <div className="hm-countdown-overlay">
                  <span className="hm-countdown-number" key={recCountdown}>{recCountdown}</span>
                </div>
              )}
              <div className="hm-badge hm-badge-live">
                <span className="hm-badge-dot" />
                Camera Active
              </div>
              <img src="/logo192.png" alt="" className="hm-watermark" />

            </div>
            <div className="hm-camera-controls">
              {hintDisplay && !allReadyDisplay && (
                <div className="hm-hint-banner">
                  <AlertCircle size={14} />
                  <span>{hintDisplay}</span>
                </div>
              )}
              <div className="hm-btn-group">
                <button className="hm-btn hm-btn-record" onClick={() => { if (countdownTimerRef.current) { clearInterval(countdownTimerRef.current); countdownTimerRef.current = null; } if (recCountdownTimerRef.current) { clearInterval(recCountdownTimerRef.current); recCountdownTimerRef.current = null; } startRecordingInternal(); }}>
                  <span className="hm-record-dot" />
                  Start Recording
                </button>
                <button className="hm-btn hm-btn-secondary" onClick={reset}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        {stage === 'recording' && !showRecComplete && (
          <div className="hm-recording">
            <div className="hm-camera-border hm-border-pulse" />
            <div className="hm-video-wrap">
              <video ref={videoRef} autoPlay playsInline muted className="hm-video"
                style={{ transform: 'scaleX(-1)', backgroundColor: '#000' }} />
              <div className="hm-video-overlay">
                <div className="hm-face-guide face-recording" />
              </div>
              <div className="hm-badge hm-badge-rec">
                <span className="hm-badge-dot-red" />
                Recording
              </div>
              <div className="hm-timer-overlay">
                <Clock size={16} />
                <span className="hm-timer-val">{String(Math.floor(recordingTime / 60)).padStart(2, '0')}:{String(recordingTime % 60).padStart(2, '0')}</span>
                <span className="hm-timer-total"> / 00:30</span>
              </div>
              <img src="/logo192.png" alt="" className="hm-watermark" />
            </div>
            <div className="hm-progress-wrap">
              <div className="hm-progress-bar">
                <div className="hm-progress-fill" style={{ width: `${(recordingTime / 30) * 100}%` }} />
              </div>
            </div>
            <p className="hm-recording-hint">Stay still and breathe normally...</p>
          </div>
        )}

        {showRecComplete && (
          <div className="hm-recording-complete">
            <div className="hm-complete-inner">
              <div className="hm-complete-check">
                <svg viewBox="0 0 24 24" width="36" height="36"><path d="M5 13l4 4L19 7" fill="none" stroke="#10B981" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="hm-check-path" /></svg>
              </div>
              <p className="hm-complete-text">Recording Complete</p>
            </div>
          </div>
        )}

        {stage === 'processing' && (
          <div className="hm-processing">
            <div className="hm-processing-spinner">
              <Activity size={32} className="hm-spin-icon" />
            </div>
            <h2 className="hm-processing-title">Analyzing Your Vitals</h2>
            <p className="hm-processing-desc">Our AI is processing your video data</p>
            <div className="hm-progress-wrap">
              <div className="hm-progress-bar">
                <div className="hm-progress-fill hm-progress-animated" style={{ width: `${progress}%` }} />
              </div>
              <span className="hm-progress-text">{Math.round(progress)}%</span>
            </div>
            <div className="hm-elapsed">
              <Clock size={14} />
              <span>Elapsed: {formatElapsed(elapsedProcessing)} — please keep this tab open</span>
            </div>
          </div>
        )}

        {stage === 'results' && results && (
          <div className="hm-results">
            <div className="hm-results-header">
              <div className="hm-success-icon">
                <CheckCircle size={36} />
              </div>
              <h2 className="hm-results-title">Analysis Complete</h2>
              <p className="hm-results-sub">Your vital signs have been successfully analyzed</p>
            </div>

            {patient && (
              <div className="hm-patient-info">
                <span className="hm-patient-info-label">Patient</span>
                <span className="hm-patient-info-name">{patient.patientName}</span>
                <span className="hm-patient-info-sep">|</span>
                <span><strong>Age:</strong> {patient.age}</span>
                <span className="hm-patient-info-sep">|</span>
                <span><strong>Gender:</strong> {patient.gender}</span>
              </div>
            )}

            <div className="hm-results-grid">
              <div className="hm-vital-card hm-vital-card-1">
                <div className="hm-vital-header">
                  <Heart size={18} />
                  <span className={`hm-status-badge ${getHeartRateStatus(results.heart_rate).color}`}>
                    {getHeartRateStatus(results.heart_rate).label}
                  </span>
                </div>
                <p className="hm-vital-label">Heart Rate</p>
                <div className="hm-vital-value">
                  <span className="hm-vital-number">{Math.round(results.heart_rate)}</span>
                  <span className="hm-vital-unit">BPM</span>
                </div>
              </div>

              <div className="hm-vital-card hm-vital-card-2">
                <div className="hm-vital-header">
                  <TrendingUp size={18} />
                  <span className={`hm-status-badge ${getBPStatus(results.bp_systolic, results.bp_diastolic).color}`}>
                    {getBPStatus(results.bp_systolic, results.bp_diastolic).label}
                  </span>
                </div>
                <p className="hm-vital-label">BP Systolic</p>
                <div className="hm-vital-value">
                  <span className="hm-vital-number">{results.bp_systolic}</span>
                  <span className="hm-vital-unit">mmHg</span>
                </div>
              </div>

              <div className="hm-vital-card hm-vital-card-3">
                <div className="hm-vital-header">
                  <TrendingDown size={18} />
                  <span className={`hm-status-badge ${getBPStatus(results.bp_systolic, results.bp_diastolic).color}`}>
                    {getBPStatus(results.bp_systolic, results.bp_diastolic).label}
                  </span>
                </div>
                <p className="hm-vital-label">BP Diastolic</p>
                <div className="hm-vital-value">
                  <span className="hm-vital-number">{results.bp_diastolic}</span>
                  <span className="hm-vital-unit">mmHg</span>
                </div>
              </div>

              <div className="hm-vital-card hm-vital-card-4">
                <div className="hm-vital-header">
                  <Activity size={18} />
                  <span className="hm-status-badge status-normal">Normal</span>
                </div>
                <p className="hm-vital-label">SpO2</p>
                <div className="hm-vital-value">
                  <span className="hm-vital-number">{results.spo2.toFixed(1)}</span>
                  <span className="hm-vital-unit">%</span>
                </div>
              </div>

              <div className="hm-vital-card hm-vital-card-5">
                <div className="hm-vital-header">
                  <Droplets size={18} />
                  <span className={`hm-status-badge ${getHemoglobinStatus(results.hemoglobin).color}`}>
                    {getHemoglobinStatus(results.hemoglobin).label}
                  </span>
                </div>
                <p className="hm-vital-label">Hemoglobin</p>
                <div className="hm-vital-value">
                  <span className="hm-vital-number">{results.hemoglobin > 0 ? results.hemoglobin.toFixed(1) : '\u2014'}</span>
                  <span className="hm-vital-unit">g/dL</span>
                </div>
              </div>

              <div className="hm-vital-card hm-vital-card-6">
                <div className="hm-vital-header">
                  <Thermometer size={18} />
                  <span className="hm-status-badge status-normal">Normal</span>
                </div>
                <p className="hm-vital-label">Respiratory Rate</p>
                <div className="hm-vital-value">
                  <span className="hm-vital-number">{results.resp_rate.toFixed(1)}</span>
                  <span className="hm-vital-unit">br/min</span>
                </div>
              </div>

              <div className="hm-vital-card hm-vital-card-7">
                <div className="hm-vital-header">
                  <Zap size={18} />
                  <span className={`hm-status-badge ${getHRVStatus(results.hrv_rmssd).color}`}>
                    {getHRVStatus(results.hrv_rmssd).label}
                  </span>
                </div>
                <p className="hm-vital-label">HRV RMSSD</p>
                <div className="hm-vital-value">
                  <span className="hm-vital-number">{Math.round(results.hrv_rmssd)}</span>
                  <span className="hm-vital-unit">ms</span>
                </div>
              </div>

              <div className="hm-vital-card hm-vital-card-8">
                <div className="hm-vital-header">
                  <BarChart3 size={18} />
                  <span className={`hm-status-badge ${getHRVStatus(results.hrv_sdnn).color}`}>
                    {getHRVStatus(results.hrv_sdnn).label}
                  </span>
                </div>
                <p className="hm-vital-label">HRV SDNN</p>
                <div className="hm-vital-value">
                  <span className="hm-vital-number">{Math.round(results.hrv_sdnn)}</span>
                  <span className="hm-vital-unit">ms</span>
                </div>
              </div>
            </div>

            <div className="hm-radar-section">
              <div className="hm-radar-chart-wrap">
                <h3 className="hm-radar-title">Overall Vital Health Overview</h3>
                <p className="hm-radar-sub">A visual comparison of your measured vital signs.</p>
                <div style={{ width: '100%', height: 340, position: 'relative' }}>
                  <SvgRadarChart />
                </div>
              </div>
              <div className="hm-summary-wrap">
                {(() => { const s = getHealthSummary(); return (
                  <>
                    <h3 className="hm-summary-title">AI Health Summary</h3>
                    <ul className="hm-summary-list">
                      {s.lines.map((line, i) => <li key={i} className="hm-summary-item">{line}</li>)}
                    </ul>
                    <div className="hm-wellness-wrap">
                      <svg className="hm-wellness-ring" viewBox="0 0 120 120" width="120" height="120">
                        <circle cx="60" cy="60" r="52" fill="none" stroke="#E2E8F0" strokeWidth="8" />
                        <circle cx="60" cy="60" r="52" fill="none" stroke="#0F766E" strokeWidth="8"
                          strokeDasharray={`${(s.score / 100) * 326.7} 326.7`}
                          strokeLinecap="round" transform="rotate(-90 60 60)"
                          style={{ transition: 'stroke-dasharray 1s ease 0.5s' }} />
                      </svg>
                      <div className="hm-wellness-score">
                        <span className="hm-wellness-num">{s.score}</span>
                        <span className="hm-wellness-total">/ 100</span>
                      </div>
                    </div>
                    <p className="hm-wellness-label">Overall Wellness Score</p>
                  </>
                ); })()}
              </div>
            </div>

            <div className="hm-scan-details">
              <h3 className="hm-scan-title">
                <Clock size={16} />
                Scan Details
              </h3>
              <div className="hm-scan-grid">
                <div className="hm-scan-item">
                  <p className="hm-scan-label">Duration</p>
                  <p className="hm-scan-val">{results.duration}s</p>
                </div>
                <div className="hm-scan-item">
                  <p className="hm-scan-label">Frames Analyzed</p>
                  <p className="hm-scan-val">{results.frames_analyzed}</p>
                </div>
                <div className="hm-scan-item">
                  <p className="hm-scan-label">Scan ID</p>
                  <p className="hm-scan-val">#{results.scan_id}</p>
                </div>
              </div>
            </div>

            <div className="hm-report-wrap">
              {patient && (
                <p className="hm-report-patient">
                  Patient: <strong>{patient.patientName}</strong> &middot; Age: {patient.age} &middot; Gender: {patient.gender}
                </p>
              )}
              <GenerateReportButton results={results} patient={patient} />
            </div>

            <div className="hm-disclaimer-banner">
              <ShieldCheck size={18} />
              <div>
                <p className="hm-disclaimer-title">Important Disclaimer</p>
                <p className="hm-disclaimer-text">
                  The displayed vital signs are AI-generated estimates intended for informational purposes only.
                  They are not intended for diagnosis, treatment, monitoring, or prevention of any disease.
                  Always consult a qualified healthcare professional for medical decisions.
                </p>
              </div>
            </div>

            <div className="hm-new-scan">
              <button className="hm-btn hm-btn-primary" onClick={reset}>
                <Camera size={18} />
                New Scan
              </button>
            </div>
          </div>
        )}
      </div>
      <PatientDetailsModal
        open={showPatientModal}
        onClose={() => setShowPatientModal(false)}
        onSubmit={handlePatientSubmit}
      />
    </div>
  );
};

export default HealthMonitorApp;
